import { FileSpreadsheet, FileUp, LoaderCircle, UploadCloud } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { extractErrorMessage, uploadFormResource } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

const SummaryCard = ({ label, testId, value }) => (
  <div className="rounded-[28px] border border-zinc-200 bg-zinc-50 px-5 py-4" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
    <p className="mt-2 text-3xl font-semibold text-zinc-900">{value}</p>
  </div>
);

export const MasterImportWizardDialog = ({ config, open, onOpenChange }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [duplicateStrategy, setDuplicateStrategy] = useState("skip");
  const [validation, setValidation] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState({ upload: false, validate: false, import: false });
  const [dragActive, setDragActive] = useState(false);

  const resetState = () => {
    setFile(null);
    setPreview(null);
    setMapping({});
    setDuplicateStrategy("skip");
    setValidation(null);
    setResult(null);
    setBusy({ upload: false, validate: false, import: false });
    setDragActive(false);
  };

  useEffect(() => {
    resetState();
  }, [config?.key, open]);

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("field_mapping", JSON.stringify(mapping));
    formData.append("duplicate_strategy", duplicateStrategy);
    return formData;
  };

  const handlePreview = async (selectedFile) => {
    if (!config || !selectedFile) return;
    try {
      setBusy((current) => ({ ...current, upload: true }));
      const formData = new FormData();
      formData.append("file", selectedFile);
      const payload = await uploadFormResource(`/imports/${config.key}/preview`, formData);
      setFile(selectedFile);
      setPreview(payload);
      setMapping(payload.suggested_mapping || {});
      setDuplicateStrategy(payload.duplicate_strategies?.[0]?.value || "skip");
      setValidation(null);
      setResult(null);
      toast.success(`Loaded ${payload.total_rows} ${config.label.toLowerCase()} row${payload.total_rows === 1 ? "" : "s"}.`);
    } catch (error) {
      toast.error(extractErrorMessage(error, `Unable to read the ${config.label.toLowerCase()} CSV.`));
    } finally {
      setBusy((current) => ({ ...current, upload: false }));
    }
  };

  const validateImport = async () => {
    if (!config || !file) return;
    try {
      setBusy((current) => ({ ...current, validate: true }));
      const payload = await uploadFormResource(`/imports/${config.key}/validate`, buildFormData());
      setValidation(payload);
      setResult(null);
      toast.success("Preview refreshed. Review rows before saving.");
    } catch (error) {
      toast.error(extractErrorMessage(error, `Unable to validate the ${config.label.toLowerCase()} rows.`));
    } finally {
      setBusy((current) => ({ ...current, validate: false }));
    }
  };

  const commitImport = async () => {
    if (!config || !file) return;
    try {
      setBusy((current) => ({ ...current, import: true }));
      const payload = await uploadFormResource(`/imports/${config.key}/commit`, buildFormData(), true);
      setResult(payload);
      toast.success(`Saved ${payload.summary.saved_rows} ${config.label.toLowerCase()} row${payload.summary.saved_rows === 1 ? "" : "s"}.`);
    } catch (error) {
      toast.error(extractErrorMessage(error, `Unable to complete the ${config.label.toLowerCase()} import.`));
    } finally {
      setBusy((current) => ({ ...current, import: false }));
    }
  };

  const validationRows = useMemo(() => validation?.rows?.slice(0, 40) || [], [validation]);

  if (!config) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-6xl overflow-hidden border-0 bg-white p-0 sm:rounded-[32px]" data-testid={`master-import-dialog-${config.key}`}>
        <div className="flex max-h-[90vh] flex-col overflow-hidden">
          <div className="border-b border-amber-100 bg-[linear-gradient(135deg,#fff7ed_0%,#ffffff_45%,#ecfccb_100%)] px-6 py-6 md:px-8">
            <DialogHeader className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Settings import wizard</p>
              <DialogTitle className="text-3xl font-semibold text-zinc-900">Import {config.label} from CSV</DialogTitle>
            </DialogHeader>
          </div>

          <div className="space-y-6 overflow-y-auto px-6 py-6 md:px-8">
            <section
              className={`rounded-[32px] border-2 border-dashed px-6 py-10 text-center transition ${dragActive ? "border-zinc-900 bg-zinc-50" : "border-zinc-300 bg-white"}`}
              data-testid={`master-import-dropzone-${config.key}`}
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragActive(false);
              }}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                setDragActive(false);
                handlePreview(event.dataTransfer.files?.[0]);
              }}
            >
              <UploadCloud className="mx-auto h-10 w-10 text-zinc-400" />
              <h2 className="mt-4 text-2xl font-semibold text-zinc-900">Drop your {config.label.toLowerCase()} CSV</h2>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white" data-testid={`master-import-upload-button-${config.key}`}>
                  {busy.upload ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                  Choose CSV file
                  <input accept=".csv" className="hidden" onChange={(event) => handlePreview(event.target.files?.[0])} type="file" />
                </label>
                <a className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900" data-testid={`master-import-template-link-${config.key}`} href={config.templatePath}>
                  <FileSpreadsheet className="h-4 w-4" /> Download sample CSV
                </a>
              </div>
              {file ? <p className="mt-4 text-sm text-zinc-500" data-testid={`master-import-selected-file-${config.key}`}>{file.name}</p> : null}
            </section>

            {preview ? (
              <section className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
                <div className="table-shell">
                  <div className="border-b border-zinc-200 px-5 py-4">
                    <h2 className="text-2xl font-semibold text-zinc-900">Field mapping</h2>
                  </div>
                  <div className="overflow-x-auto px-5 py-5">
                    <table className="data-table" data-testid={`master-import-mapping-table-${config.key}`}>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>CSV column</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.field_options.map((field) => (
                          <tr key={field.key}>
                            <td>
                              <p className="font-medium text-zinc-900">{field.label}</p>
                              <p className="mt-1 text-xs text-zinc-500">{field.required ? "Recommended" : "Optional"}</p>
                            </td>
                            <td>
                              <select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid={`master-import-mapping-${config.key}-${field.key}`} onChange={(event) => setMapping((current) => ({ ...current, [field.key]: event.target.value || null }))} value={mapping[field.key] || ""}>
                                <option value="">Ignore for now</option>
                                {preview.headers.map((header) => <option key={`${config.key}-${field.key}-${header}`} value={header}>{header}</option>)}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="panel-surface px-5 py-5" data-testid={`master-import-options-card-${config.key}`}>
                    <h2 className="text-2xl font-semibold text-zinc-900">Duplicate handling</h2>
                    <div className="mt-4 space-y-3">
                      {preview.duplicate_strategies.map((option) => (
                        <label className="flex items-start gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-4" key={option.value}>
                          <input checked={duplicateStrategy === option.value} data-testid={`master-import-duplicate-${config.key}-${option.value}`} name={`duplicate-strategy-${config.key}`} onChange={() => setDuplicateStrategy(option.value)} type="radio" />
                          <span className="text-sm text-zinc-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    <button className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white" data-testid={`master-import-validate-button-${config.key}`} onClick={validateImport} type="button">
                      {busy.validate ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                      Validate rows
                    </button>
                  </div>

                  <div className="panel-surface px-5 py-5" data-testid={`master-import-preview-card-${config.key}`}>
                    <h2 className="text-2xl font-semibold text-zinc-900">CSV preview</h2>
                    <div className="mt-4 overflow-x-auto">
                      <table className="data-table text-xs" data-testid={`master-import-preview-table-${config.key}`}>
                        <thead>
                          <tr>
                            {preview.headers.map((header) => <th key={header}>{header}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {preview.sample_rows.map((row, index) => (
                            <tr key={`${config.key}-sample-${index}`}>
                              {preview.headers.map((header) => <td key={`${config.key}-${index}-${header}`}>{row[header] || "—"}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </section>
            ) : null}

            {validation ? (
              <section className="space-y-6" data-testid={`master-import-validation-section-${config.key}`}>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                  <SummaryCard label="Total rows" testId={`master-import-total-rows-${config.key}`} value={validation.summary.total_rows} />
                  <SummaryCard label="Ready to save" testId={`master-import-ready-rows-${config.key}`} value={validation.summary.importable_rows} />
                  <SummaryCard label="Create" testId={`master-import-create-rows-${config.key}`} value={validation.summary.create_rows} />
                  <SummaryCard label="Update" testId={`master-import-update-rows-${config.key}`} value={validation.summary.update_rows} />
                  <SummaryCard label="Failed" testId={`master-import-failed-rows-${config.key}`} value={validation.summary.failed_rows} />
                </div>

                <div className="table-shell">
                  <div className="flex flex-col gap-3 border-b border-zinc-200 px-5 py-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h2 className="text-2xl font-semibold text-zinc-900">Validated row preview</h2>
                      <p className="mt-1 text-sm text-zinc-500">Showing the first {validationRows.length} rows from the validation result.</p>
                    </div>
                    <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-zinc-400" data-testid={`master-import-commit-button-${config.key}`} disabled={!validation.summary.importable_rows || busy.import} onClick={commitImport} type="button">
                      {busy.import ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                      Save successful rows
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="data-table" data-testid={`master-import-validation-table-${config.key}`}>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Record</th>
                          <th>Match field</th>
                          <th>Action</th>
                          <th>Match detail</th>
                          <th>Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationRows.map((row) => (
                          <tr key={`${config.key}-validated-${row.row_number}`}>
                            <td>{row.row_number}</td>
                            <td>{row.record_label || "—"}</td>
                            <td>{row.match_label || "—"}</td>
                            <td>{row.action}</td>
                            <td>{row.match_reason || "—"}</td>
                            <td>
                              {row.errors.length ? row.errors.map((error) => <p className="text-xs text-red-600" key={`${config.key}-${row.row_number}-${error}`}>{error}</p>) : <span className="text-xs font-semibold text-emerald-600">Ready</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            ) : null}

            {result ? (
              <section className="panel-surface px-5 py-5" data-testid={`master-import-result-card-${config.key}`}>
                <h2 className="text-2xl font-semibold text-zinc-900">Import summary</h2>
                <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                  <SummaryCard label="Total rows" testId={`master-import-result-total-${config.key}`} value={result.summary.total_rows} />
                  <SummaryCard label="Saved" testId={`master-import-result-saved-${config.key}`} value={result.summary.saved_rows} />
                  <SummaryCard label="Created" testId={`master-import-result-created-${config.key}`} value={result.summary.create_rows} />
                  <SummaryCard label="Updated" testId={`master-import-result-updated-${config.key}`} value={result.summary.update_rows} />
                  <SummaryCard label="Skipped / failed" testId={`master-import-result-skipped-${config.key}`} value={result.summary.skipped_rows + result.summary.failed_rows} />
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-700" data-testid={`master-import-start-over-${config.key}`} onClick={resetState} type="button">
                    Start another import
                  </button>
                </div>
              </section>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};